import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '../types';
import { getGraphQLClient, GET_ME } from '../lib/graphql';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUserLanguage: (language: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  const login = (newToken: string, newUser: User) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(newUser);
    setIsLoading(false);
    if (newUser.language) {
      i18n.changeLanguage(newUser.language);
    }
  };

  const updateUserLanguage = (language: string) => {
    if (user) {
      setUser({ ...user, language });
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    console.log('[AuthProvider] refreshUser called, token:', token ? 'exists' : 'null');

    if (!token) {
      console.log('[AuthProvider] No token, setting isLoading false');
      setIsLoading(false);
      setUser(null);
      return;
    }

    // If user is already set (e.g., from login), don't refresh
    if (user) {
      console.log('[AuthProvider] User already set, skipping refresh');
      setIsLoading(false);
      return;
    }

    console.log('[AuthProvider] Fetching user data...');
    setIsLoading(true);
    try {
      const client = getGraphQLClient();
      const data = await client.request<{ me: User | null }>(GET_ME);

      if (data.me) {
        console.log('[AuthProvider] Got user data:', data.me.email);
        setUser(data.me);
        if (data.me.language) {
          i18n.changeLanguage(data.me.language);
        }
      } else {
        console.log('[AuthProvider] No user data, logging out');
        logout();
      }
    } catch (error) {
      console.error('[AuthProvider] Error fetching user:', error);
      logout();
    } finally {
      setIsLoading(false);
      console.log('[AuthProvider] Finished refreshUser');
    }
  };

  useEffect(() => {
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refreshUser,
        updateUserLanguage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
